import get from "lodash/get";
import noop from "lodash/noop";
import {
  FIELD_ID_KEY,
  FIELD_POST_UNTIL_KEY,
  FIELD_STATE_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useGetCustomFieldsToSave } from "PFApp/hooks/activity_form/use_custom_fields_to_save";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import {
  useManageRolesStore,
  useManageRolesStoreActions,
  useManageRolesStoreContext
} from "PFApp/stores/manage_roles";
import { UnsavedEdit } from "PFApp/stores/manage_roles/manage_roles_store.types";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useActivityUpdate } from "PFCore/hooks/queries/activity";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { createActivity } from "PFCore/services/activities";
import { Id } from "PFCore/types";
import { RefObject, useCallback, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { buildRoleMap, findRoleFormPath } from "../manage_roles.utils";
import { DRAFT_UNIQUE_ID_PREFIX } from "../manage_roles_constants";
import { isEmptyValue } from "./use_form_unsaved_edits_tracker.utils";
import { useRevertEmptyDraftFields } from "./use_revert_empty_draft_fields";
import {
  buildCreatePayload,
  buildRolePayload,
  CONNECTED_FIELD_GROUPS,
  getFormErrorFieldNames,
  getUnsavedEditsWithStatus,
  groupEditsByRole,
  matchErrorFieldNamesToKeys,
  resolveErrorFieldValue
} from "./use_unsaved_edits_sync.utils";

const DEBOUNCE_MS = 1000;
const MAX_WAIT_MS = 5000;

export const MANAGE_ROLES_SYNC_SUCCESS_GROWL_ID = getGrowlId("manage-roles-sync-success");
export const MANAGE_ROLES_SYNC_ERROR_GROWL_ID = getGrowlId("manage-roles-sync-error");
const REVERTED_RESULT = "reverted";
const DRAFT_REQUIRED_FIELDS = [FIELD_POST_UNTIL_KEY];
const MANAGE_ROLES_REQUIRED_FIELD_GROWL_ID = getGrowlId("manage-roles-required-field");

type UseUnsavedEditsSync = {
  requiredKeysRef?: RefObject<Map<number, Set<string>>>;
};

export const useUnsavedEditsSync = ({ requiredKeysRef }: UseUnsavedEditsSync = {}) => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });

  const { getValues, setValue } = useFormContext<ManageRolesMultiForm>();
  const {
    markInflight,
    markFailed,
    markKeysAsErrored,
    clearEdits,
    remapRoleEdits,
    addNewActivityIds,
    addUnsavedEdit
  } = useManageRolesStoreActions();
  const storeApi = useManageRolesStoreContext();
  const currentPage = useManageRolesStore((state) => state.currentPage);
  const { showGrowl } = useGrowlStoreActions();
  const { getCustomFieldsToSave } = useGetCustomFieldsToSave();
  const { data: currentProfile } = useCurrentProfile();
  const { getAllErrorsByType } = useResponseErrors();
  const { update: updateActivity } = useActivityUpdate();
  const { revertEmptyDraftFields } = useRevertEmptyDraftFields({
    growlId: MANAGE_ROLES_REQUIRED_FIELD_GROWL_ID
  });

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const maxWaitTimerRef = useRef<NodeJS.Timeout | null>(null);
  // scheduleSyncRef breaks a circular dependency: runSync calls scheduleSyncRef.current (to reschedule
  // after partial syncs) and scheduleSync calls runSync (to trigger the actual sync). Defining both as
  // useCallback would create a dependency cycle that React cannot resolve. The ref is updated each render
  // via the useEffect below so it always holds the latest scheduleSync without being a direct dependency.
  const scheduleSyncRef = useRef<() => void>(noop);
  const syncPausedRef = useRef<boolean>(false);
  const pendingCreateRoleIdsRef = useRef<Set<Id>>(new Set());

  const cancelTimers = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (maxWaitTimerRef.current !== null) {
      clearTimeout(maxWaitTimerRef.current);
      maxWaitTimerRef.current = null;
    }
  }, []);

  const syncOneRole = useCallback(
    async (
      roleId: Id,
      keys: string[],
      edits: UnsavedEdit[],
      roleValues: ManageRolesFormType
    ): Promise<typeof REVERTED_RESULT | void> => {
      if (!roleValues.id) {
        const payload = buildCreatePayload(roleValues, getCustomFieldsToSave, currentProfile, edits);
        pendingCreateRoleIdsRef.current.add(roleValues.uniqueId);
        try {
          const created = await createActivity(payload);
          const path = findRoleFormPath(getValues(), roleValues.uniqueId);
          if (path) {
            const idPath = `subtemplates.${path.subtemplateIndex}.roles.${path.roleIndex}.${FIELD_ID_KEY}`;
            setValue(idPath as any, created.id as any);
          }
          addNewActivityIds([created.id]);
          const stillInflightKeys = keys.filter(
            (key) => storeApi.getState().unsavedEdits[key]?.status === "inflight"
          );
          clearEdits(stillInflightKeys);
          remapRoleEdits(roleValues.uniqueId, created.id);
        } catch (error) {
          const errors = getAllErrorsByType(error) ?? {};
          const errorFieldNames = getFormErrorFieldNames(errors);

          if (errorFieldNames.length === 0) {
            markFailed(keys);
          } else {
            const { remainingErrorFieldNames } = revertEmptyDraftFields(
              roleId,
              errorFieldNames,
              edits,
              roleValues
            );
            const errorKeys = new Set(remainingErrorFieldNames.map((fieldName) => `${roleId}.${fieldName}`));
            clearEdits(keys.filter((key) => !errorKeys.has(key)));

            remainingErrorFieldNames.forEach((fieldName) => {
              if (storeApi.getState().unsavedEdits[`${roleId}.${fieldName}`]?.status === "unsaved") {
                return;
              }
              addUnsavedEdit(
                roleId,
                fieldName,
                resolveErrorFieldValue(roleValues, fieldName),
                undefined,
                currentPage,
                "failed"
              );
            });

            if (remainingErrorFieldNames.length === 0) {
              return REVERTED_RESULT;
            }
          }

          throw new Error(`Role ${roleValues.uniqueId} create failed`, { cause: error });
        } finally {
          pendingCreateRoleIdsRef.current.delete(roleValues.uniqueId);
        }
        return;
      }

      const payload = buildRolePayload(roleValues, edits);

      if (Object.keys(payload).length <= 1 && "id" in payload) {
        clearEdits(keys);
        return;
      }

      try {
        await updateActivity(payload);
        clearEdits(keys);
      } catch (error) {
        markFailed(keys);

        const errors = getAllErrorsByType(error) ?? {};
        const errorFieldNames = getFormErrorFieldNames(errors);
        const { revertedKeys, remainingErrorFieldNames } = revertEmptyDraftFields(
          roleId,
          errorFieldNames,
          edits,
          roleValues
        );
        clearEdits(revertedKeys);
        const { matchedKeys, unmatchedFieldNames } = matchErrorFieldNamesToKeys(
          remainingErrorFieldNames,
          keys.filter((key) => !revertedKeys.includes(key))
        );

        markKeysAsErrored(matchedKeys);

        unmatchedFieldNames.forEach((fieldName) => {
          addUnsavedEdit(
            roleId,
            fieldName,
            resolveErrorFieldValue(roleValues, fieldName),
            undefined,
            currentPage,
            "failed"
          );
        });

        if (remainingErrorFieldNames.length === 0 && keys.every((key) => revertedKeys.includes(key))) {
          return REVERTED_RESULT;
        }

        throw new Error(`Role ${roleId} update failed`, { cause: error });
      }
    },
    [
      addNewActivityIds,
      addUnsavedEdit,
      clearEdits,
      currentPage,
      currentProfile,
      getAllErrorsByType,
      getCustomFieldsToSave,
      getValues,
      markFailed,
      markKeysAsErrored,
      remapRoleEdits,
      revertEmptyDraftFields,
      setValue,
      storeApi,
      updateActivity
    ]
  );

  const performSync = useCallback(async (): Promise<boolean> => {
    const unsavedOnly = Object.fromEntries(
      Object.entries(getUnsavedEditsWithStatus(storeApi.getState().unsavedEdits, "unsaved")).filter(
        ([, edit]) => !pendingCreateRoleIdsRef.current.has(edit.roleId)
      )
    );
    // Before marking inflight: check each unsaved edit for required fields that are now empty.
    // If a required field's current form value is empty, revert it to its previous value and
    // drop the edit — no API call should be triggered for an invalid empty value.
    const requiredKeysMap = requiredKeysRef?.current;

    if (requiredKeysMap && requiredKeysMap.size > 0) {
      const currentValues = getValues();
      const keysToRevert: string[] = [];

      Object.entries(unsavedOnly).forEach(([key, edit]) => {
        if (keysToRevert.includes(key)) {
          // Already discarded as a connected field of an earlier entry in this loop.
          return;
        }

        const path = findRoleFormPath(currentValues, edit.roleId);
        if (!path) {
          return;
        }

        const { subtemplateIndex, roleIndex } = path;
        const role = currentValues.subtemplates?.[subtemplateIndex]?.roles?.[roleIndex];
        const isDraftRoleId =
          typeof edit.roleId === "string" && edit.roleId.startsWith(DRAFT_UNIQUE_ID_PREFIX);
        const isDraftRoleState = get(role, FIELD_STATE_KEY) === "draft";
        if ((isDraftRoleId || isDraftRoleState) && !DRAFT_REQUIRED_FIELDS.includes(edit.fieldName)) {
          return;
        }

        const subtemplateRequiredKeys = requiredKeysMap.get(subtemplateIndex);

        if (subtemplateRequiredKeys?.has(edit.fieldName)) {
          const fullRolePath = `subtemplates.${subtemplateIndex}.roles.${roleIndex}` as const;
          const fullValuePath = `${fullRolePath}.${edit.fieldName}` as const;
          const currentValue = getValues(fullValuePath as any);

          if (isEmptyValue(currentValue)) {
            setValue(fullValuePath as any, edit.previousValue as any);
            keysToRevert.push(key);

            // Connected fields must be reverted as a whole, otherwise the connected field's pending edit
            // would still be sent, applying half of a rejected change.
            const connectedGroup = CONNECTED_FIELD_GROUPS.find((group) => group.includes(edit.fieldName));
            connectedGroup?.forEach((connectedField) => {
              if (connectedField === edit.fieldName) {
                return;
              }
              const connectedKey = `${edit.roleId}.${connectedField}`;
              const connectedEdit = unsavedOnly[connectedKey];
              if (connectedEdit) {
                setValue(`${fullRolePath}.${connectedField}` as any, connectedEdit.previousValue as any);
                keysToRevert.push(connectedKey);
              }
            });
          }
        }
      });

      if (keysToRevert.length > 0) {
        clearEdits(keysToRevert);

        keysToRevert.forEach((key) => {
          delete unsavedOnly[key];
        });

        showGrowl({
          message: t("requiredFieldRevert"),
          kind: "error",
          id: MANAGE_ROLES_REQUIRED_FIELD_GROWL_ID,
          hidePreviousByTypeAndId: true
        });
      }
    }

    const editKeys = Object.keys(unsavedOnly);
    if (editKeys.length === 0) {
      return true;
    }

    markInflight(editKeys);

    const roleMap = buildRoleMap(getValues());
    const byRole = groupEditsByRole(unsavedOnly);
    const results = await Promise.allSettled(
      Array.from(byRole.entries()).map(([roleId, { keys, edits }]) => {
        const roleValues = roleMap.get(roleId);
        if (!roleValues) {
          clearEdits(keys);
          return Promise.resolve();
        }
        return syncOneRole(roleId, keys, edits, roleValues);
      })
    );

    const failuresCount = results.filter((result) => result.status === "rejected").length;
    const revertedCount = results.filter(
      (result) => result.status === "fulfilled" && result.value === REVERTED_RESULT
    ).length;
    const successesCount = byRole.size - failuresCount - revertedCount;

    if (successesCount > 0) {
      showGrowl({
        message: t("saveSuccess", { count: successesCount }),
        kind: "success",
        id: MANAGE_ROLES_SYNC_SUCCESS_GROWL_ID,
        hidePreviousByTypeAndId: true
      });
    }
    if (failuresCount > 0) {
      showGrowl({
        message: t("saveError"),
        kind: "alert",
        id: MANAGE_ROLES_SYNC_ERROR_GROWL_ID,
        hidePreviousByTypeAndId: true
      });
    }

    return failuresCount === 0;
  }, [clearEdits, getValues, showGrowl, markInflight, requiredKeysRef, setValue, storeApi, syncOneRole, t]);

  const runSync = useCallback(async () => {
    cancelTimers();
    await performSync();
    const stillUnsaved = Object.values(storeApi.getState().unsavedEdits).some(
      (edit) => edit.status === "unsaved"
    );
    if (stillUnsaved) {
      scheduleSyncRef.current();
    }
  }, [cancelTimers, performSync, storeApi]);

  const scheduleSync = useCallback(() => {
    if (syncPausedRef.current) {
      return;
    }
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(
      () => runSync().catch((err) => console.error("Unexpected sync error:", err)),
      DEBOUNCE_MS
    );
    if (maxWaitTimerRef.current === null) {
      maxWaitTimerRef.current = setTimeout(
        () => runSync().catch((err) => console.error("Unexpected sync error:", err)),
        MAX_WAIT_MS
      );
    }
  }, [runSync]);

  useEffect(() => {
    scheduleSyncRef.current = scheduleSync;
  }, [scheduleSync]);

  useEffect(() => {
    let prevUnsavedEdits = storeApi.getState().unsavedEdits;
    const unsubscribe = storeApi.subscribe((state) => {
      if (state.unsavedEdits !== prevUnsavedEdits) {
        prevUnsavedEdits = state.unsavedEdits;
        scheduleSyncRef.current();
      }
    });
    return () => {
      unsubscribe();
      cancelTimers();
    };
  }, [cancelTimers, storeApi]);

  const performSyncRef = useRef(performSync);
  useEffect(() => {
    performSyncRef.current = performSync;
  }, [performSync]);

  const flushUnsavedEdits = useCallback(async (): Promise<boolean> => {
    cancelTimers();
    return performSyncRef.current();
  }, [cancelTimers]);

  const pauseSync = useCallback(() => {
    cancelTimers();
    syncPausedRef.current = true;
  }, [cancelTimers]);

  const resumeSync = useCallback(() => {
    syncPausedRef.current = false;

    const hasUnsaved = Object.values(storeApi.getState().unsavedEdits).some(
      (edit) => edit.status === "unsaved"
    );

    if (hasUnsaved) {
      scheduleSyncRef.current();
    }
  }, [storeApi]);

  return { flushUnsavedEdits, pauseSync, resumeSync };
};
